/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  configureStore,
  combineReducers,
  Reducer,
  ThunkDispatch,
  AnyAction,
} from '@reduxjs/toolkit';
import { ServiceIdentifier, Container } from 'inversify';

import { inject, Inject } from '@vk-hr-tek/core/ioc';
import { CsatService, type Csat } from '@vk-hr-tek/core/csat';
import { AppError } from '@vk-hr-tek/core/error';
import { downloadNotificationReducer as downloadNotification } from '@vk-hr-tek/core/download-notification';
import { notificationReducer as notifications } from '@vk-hr-tek/core/notifications';
import { cryptoReducer as crypto } from '@vk-hr-tek/core/crypto';
import { chatReducer as chat } from '@vk-hr-tek/core/chat';
import { translationsReducer as translations } from '@vk-hr-tek/core/translations';
import {
  HTTP_BAD_GATEWAY_STATUS,
  HTTP_NOT_UNAUTHORIZED_STATUS,
  HTTP_SERVICE_UNAVAILABLE_STATUS,
  HTTP_FORBIDDEN_STATUS,
} from '@vk-hr-tek/core/http';
import { UpdatorService } from '@vk-hr-tek/core/updator';

import { authReducer as auth, logout } from '../auth/slice';
import { userReducer as user } from '../user/slice';
import { policyReducer as policy } from '../policy/slice';
import { dashboardReducer as dashboard } from '../dashboard/slice';
import { absencesReducer as absences } from '../absences/slice';
import { reviewReducer as review } from '../review/slice';
import { bellNotificationsReducer as bellNotifications } from '../bell-notifications/slice';
import { processCatalogReducer as processCatalog } from '../process-catalog/slice';
import {
  creationEventReducer as creationEvent,
  initialState,
} from '../event-creation/slice';
import {
  layoutReducer as layout,
  setServiceUnavailable,
} from '../layout/slice';
import { eventsReducer as events } from '../events/slice';
import { filtersPresetReducer as filtersPreset } from '../filtersPreset/slice';
import { settingsReducer as settings } from '../settings/slice';
import { eventsActionsReducer as eventsActions } from '../action-snackbar/slice';
import { userSettingsReducer as userSettings } from '../user-settings/slice';

import { isReloadError } from './constants';

export const createStore = (container?: Container) => {
  const staticReducers = {
    events,
    auth,
    user,
    policy,
    processCatalog,
    notifications,
    downloadNotification,
    crypto,
    layout,
    chat,
    dashboard,
    translations,
    absences,
    creationEvent,
    filtersPreset,
    bellNotifications,
    review,
    settings,
    eventsActions,
    userSettings,
  };

  type StaticState = {
    [K in keyof typeof staticReducers]: ReturnType<(typeof staticReducers)[K]>;
  };

  const asyncReducers: Record<string, Reducer> = {};

  const createReducer = () =>
    combineReducers({
      ...staticReducers,
      ...asyncReducers,
    });

  const csatMiddleware = () => (next: any) => (action: any) => {
    if (
      action.type.endsWith('fulfilled') &&
      !action.type.startsWith('layout') &&
      !action.type.startsWith('bellNotifications') &&
      !action.type.startsWith('core')
    ) {
      const csat = inject<Csat>(CsatService);
      csat.registerEvent(
        `action:${action.type.replace('/fulfilled', '').replaceAll('/', ':')}`,
      );
    }
    return next(action);
  };

  const logoutMiddleware = (store: any) => (next: any) => (action: any) => {
    if (
      action.type.endsWith('/rejected') &&
      !action.type.startsWith('auth/') &&
      action.payload &&
      action.payload.status === HTTP_NOT_UNAUTHORIZED_STATUS
    ) {
      store.dispatch(logout({ skipLogout: true }));
    }

    return next(action);
  };

  const reloadMiddleware = () => (next: any) => (action: any) => {
    if (
      action.type.endsWith('/rejected') &&
      action.payload &&
      action.payload.status === HTTP_FORBIDDEN_STATUS &&
      isReloadError(action.payload.errorCode)
    ) {
      inject(UpdatorService, container).forceReload();
    }

    return next(action);
  };

  const errorMiddleware = (store: any) => (next: any) => (action: any) => {
    if (
      action.payload &&
      [HTTP_BAD_GATEWAY_STATUS, HTTP_SERVICE_UNAVAILABLE_STATUS].includes(
        action.payload.status,
      )
    ) {
      store.dispatch(setServiceUnavailable(true));
    }

    return next(action);
  };

  const rootReducer = () => (state: StaticState | undefined, action: any) => {
    if (
      action.type === 'auth/logout/fulfilled' ||
      action.type === 'core/redux/reset-store'
    ) {
      state = undefined;
    }

    if (action.type === 'layout/resetState') {
      state = {
        dashboard: state!.dashboard,
        user: state!.user,
        layout: state!.layout,
        bellNotifications: state!.bellNotifications,
        translations: state!.translations,
      } as StaticState;
    }

    if (action.type === 'user/setUserRole/pending') {
      state = { user: state!.user, auth: state!.auth } as StaticState;
    }

    if (action.type === 'user/changeRole') {
      state = {
        user: state!.user,
        layout: state!.layout,
        creationEvent: {
          ...initialState,
          createEventData: state!.creationEvent?.createEventData,
        },
        bellNotifications: state!.bellNotifications,
        translations: state!.translations,
      } as StaticState;
    }

    return createReducer()(state, action);
  };

  const store = configureStore({
    reducer: rootReducer(),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: {
            inject: <T>(service: ServiceIdentifier<T>) =>
              inject(service, container),
          },
        },
      })
        .concat(logoutMiddleware)
        .concat(csatMiddleware)
        .concat(errorMiddleware)
        .concat(reloadMiddleware),
  });

  const injectReducer = (key: string, asyncReducer: Reducer) => {
    if (!asyncReducers[key]) {
      asyncReducers[key] = asyncReducer;
      store.replaceReducer(rootReducer());
    }
  };

  return { store, injectReducer };
};

type Store = ReturnType<typeof createStore>['store'];
export type AppDispatch = Store['dispatch'];
export type RootState = ReturnType<Store['getState']>;
export type AppDispatchWithLocal<T> = ThunkDispatch<
  T,
  { inject: Inject },
  AnyAction
>;

export interface ThunkExtra<T = RootState> {
  extra: {
    inject: Inject;
  };
  state: T;
  dispatch: AppDispatchWithLocal<T>;
  rejectValue: AppError;
}

export interface ThunkValuesAndActions<T> {
  values: T;
  actions: {
    resolve: (value: unknown) => void;
    reject: (value: unknown) => void;
  };
}
